// References.js - Adam International Academy Documents Version
import React, { useState, useEffect, useRef } from 'react';
import './References.css';

const References = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [visibleSections, setVisibleSections] = useState({});
  const sectionRefs = useRef({});

  // Intersection Observer for scroll animations - DÜZELTİLDİ
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setVisibleSections(prev => ({
              ...prev,
              [entry.target.dataset.section]: true
            }));
          }
        });
      },
      { threshold: 0.1, rootMargin: '50px' } // threshold düşürüldü, rootMargin eklendi
    );

    Object.values(sectionRefs.current).forEach(ref => {
      if (ref) observer.observe(ref);
    });

    return () => {
      Object.values(sectionRefs.current).forEach(ref => {
        if (ref) observer.unobserve(ref);
      });
    };
  }, []);

  // TÜM SECTION'LARI BAŞLANGIÇTA GÖRÜNÜR YAP
  useEffect(() => {
    // Sayfa yüklendiğinde tüm section'ları görünür yap
    const allSections = [
      'featured', 'main', 'journal', 'stats', 'newsletter', 'resources'
    ];
    
    const initialVisible = {};
    allSections.forEach(section => {
      initialVisible[section] = true;
    });
    
    setVisibleSections(initialVisible);
  }, []);

  // Modal scroll lock
  useEffect(() => {
    if (isModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isModalOpen]);

  // Categories from documents
  const categories = [
    { id: 'all', name: 'All Publications', icon: '📚', count: 15 },
    { id: 'journal', name: 'Adam International Journal', icon: '📖', count: 4 },
    { id: 'research', name: 'Research Papers', icon: '🔬', count: 5 },
    { id: 'reports', name: 'Annual Reports', icon: '📊', count: 3 },
    { id: 'guides', name: 'Capacity Development Guides', icon: '📘', count: 3 }
  ];

  // Articles and publications from Adam International Academy documents
  const articles = [
    // Adam International Journal - Volume 1, Issue 1
    {
      id: 1,
      title: 'Sustainable Development Goals (SDG 2030): Implementation Strategies for Developing Economies',
      author: 'Prof. Dr. Abdulnasir Munir, Prof. Dr. Ayşe Demir',
      date: '2026-01-15',
      category: 'journal',
      journal: 'Adam International Journal, Volume 1, Issue 1',
      readTime: '18 min read',
      excerpt: 'Comprehensive analysis of SDG implementation strategies in developing economies, with case studies from Africa and the Middle East.',
      content: 'This groundbreaking research examines the challenges and opportunities for implementing the 2030 Agenda for Sustainable Development in developing economies. The study analyzes data from 15 countries and identifies key success factors including policy alignment, capacity development, and multi-stakeholder partnerships. Special attention is given to SDG 4 (Quality Education) and SDG 8 (Decent Work and Economic Growth). The research concludes with practical recommendations for policymakers and development practitioners.',
      tags: ['SDG 2030', 'Sustainable Development', 'Policy Analysis', 'Developing Economies'],
      featured: true,
      issn: '2757-8912',
      pages: 124,
      doi: '10.5281/zenodo.14725836'
    },
    {
      id: 2,
      title: 'Multilingual Education: Strategies for Culturally Sensitive Capacity Development',
      author: 'Prof. Dr. Samir Albuny, Dr. Amanda White',
      date: '2026-01-15',
      category: 'journal',
      journal: 'Adam International Journal, Volume 1, Issue 1',
      readTime: '15 min read',
      excerpt: 'Exploring best practices in multilingual education and content adaptation for diverse cultural contexts.',
      content: 'This paper presents a comprehensive framework for designing and delivering effective multilingual education programs. Drawing on case studies from Africa, the Middle East, and Europe, the research examines strategies for content adaptation, culturally sensitive instructional design, and assessment in multiple languages. The study highlights the importance of mother-tongue instruction in early education and provides guidelines for developing multilingual learning materials.',
      tags: ['Multilingual Education', 'Capacity Development', 'Cultural Sensitivity', 'SDG 4'],
      featured: true,
      issn: '2757-8912',
      pages: 18
    },
    
    // Adam International Journal - Volume 1, Issue 2
    {
      id: 3,
      title: 'Artificial Intelligence for Sustainable Development: Opportunities and Ethical Challenges',
      author: 'Prof. Dr. Kevin Zhang, Prof. Dr. Samir Albuny',
      date: '2026-04-20',
      category: 'journal',
      journal: 'Adam International Journal, Volume 1, Issue 2',
      readTime: '20 min read',
      excerpt: 'Examining the role of AI in achieving SDG targets while addressing ethical considerations and bias mitigation.',
      content: 'This research explores the transformative potential of artificial intelligence in advancing sustainable development goals. The study examines AI applications in healthcare, education, agriculture, and climate action, while critically analyzing ethical challenges including algorithmic bias, data privacy, and digital divide. The paper proposes a framework for responsible AI implementation in development contexts, emphasizing the need for inclusive design and local capacity building.',
      tags: ['Artificial Intelligence', 'SDG 2030', 'Ethics', 'Digital Transformation'],
      featured: false,
      issn: '2757-8912',
      pages: 22
    },
    {
      id: 4,
      title: 'Women in Leadership: Breaking Barriers and Driving Change',
      author: 'Prof. Dr. Nancy Adams, Prof. Dr. Ayşe Demir',
      date: '2026-04-20',
      category: 'journal',
      journal: 'Adam International Journal, Volume 1, Issue 2',
      readTime: '16 min read',
      excerpt: 'Research on challenges and opportunities for women leaders, with recommendations for organizational change.',
      content: 'This study examines the persistent barriers facing women in leadership positions and identifies successful strategies for overcoming them. Based on interviews with 50 women leaders across 15 countries, the research explores themes of mentorship, work-life integration, and organizational culture. The paper presents the LEADWomen framework for developing female leadership talent and promoting gender equality in organizations.',
      tags: ['Women in Leadership', 'Gender Equality', 'LEADWomen', 'SDG 5'],
      featured: true,
      issn: '2757-8912',
      pages: 16
    },

    // Research Papers
    {
      id: 5,
      title: 'Capacity Development Best Practices: A Guide for Practitioners',
      author: 'Dr. M. Haydar, Prof. Dr. Sarah Wilson',
      date: '2026-02-28',
      category: 'guides',
      readTime: '25 min read',
      excerpt: 'Comprehensive guide for designing and delivering effective capacity development interventions.',
      content: 'This practical guide draws on Adam International Academy\'s five years of experience in capacity development across 30 countries. It provides step-by-step methodologies for needs assessment, program design, implementation, and impact evaluation. The guide includes templates, checklists, and case studies from successful capacity development initiatives in Africa, Asia, and the Middle East.',
      tags: ['Capacity Development', 'Training', 'Best Practices', 'SDG 4'],
      featured: true,
      pages: 68
    },
    {
      id: 6,
      title: 'Economic Feasibility Studies: Methodology and Applications',
      author: 'Prof. Dr. Abdulnasir Munir',
      date: '2026-03-10',
      category: 'research',
      readTime: '22 min read',
      excerpt: 'Advanced methodology for conducting economic feasibility studies for development projects.',
      content: 'This research paper presents a comprehensive methodology for conducting economic feasibility studies in development contexts. It covers market analysis, financial modeling, risk assessment, and social impact evaluation. The methodology is illustrated through case studies of infrastructure projects, educational initiatives, and renewable energy investments.',
      tags: ['Economic Feasibility', 'Project Evaluation', 'Development Economics'],
      featured: false
    },
    {
      id: 7,
      title: 'International Commercial Arbitration: Procedures and Best Practices',
      author: 'Prof. Dr. Najib Al-Rahman',
      date: '2026-02-15',
      category: 'research',
      readTime: '20 min read',
      excerpt: 'Comprehensive overview of international arbitration procedures and dispute resolution mechanisms.',
      content: 'This paper examines the legal frameworks and procedural rules governing international commercial arbitration. It provides practical guidance on arbitration agreements, arbitrator selection, hearing procedures, and award enforcement. The research includes analysis of recent cases and emerging trends in international dispute resolution.',
      tags: ['Arbitration', 'International Law', 'Dispute Resolution'],
      featured: false
    },

    // Annual Reports
    {
      id: 8,
      title: 'Annual Report 2024: Impact and Achievements',
      author: 'Adam International Academy',
      date: '2025-02-28',
      category: 'reports',
      readTime: '12 min read',
      excerpt: 'Comprehensive report on AIA\'s activities, partnerships, and contributions to SDG 2030 in 2024.',
      content: 'This annual report details Adam International Academy\'s achievements in 2024, including the launch of 8 new programs, partnerships with 5 universities, and capacity development initiatives reaching 2,500+ professionals across 30 countries. The report includes financial statements, impact metrics, and case studies of successful interventions.',
      tags: ['Annual Report', 'Impact', 'Achievements', '2024'],
      featured: true
    },
    {
      id: 9,
      title: 'Annual Report 2025: Global Expansion and Strategic Partnerships',
      author: 'Adam International Academy',
      date: '2026-02-15',
      category: 'reports',
      readTime: '14 min read',
      excerpt: 'Highlights of AIA\'s global expansion, new partnerships, and program developments in 2025.',
      content: 'The 2025 annual report showcases Adam International Academy\'s expansion into African markets, new partnerships with UNDP and UNESCO, and the launch of the Adam International Journal. The report also highlights the growth of our faculty to 100+ experts from 30 countries and the development of multilingual education programs.',
      tags: ['Annual Report', 'Global Expansion', 'Partnerships', '2025'],
      featured: true
    },

    // Capacity Development Guides
    {
      id: 10,
      title: 'SDG 4 Implementation Handbook: Quality Education for All',
      author: 'AIA Sustainable Development Team',
      date: '2026-01-30',
      category: 'guides',
      readTime: '30 min read',
      excerpt: 'Comprehensive handbook for organizations seeking to align their education programs with SDG 4.',
      content: 'This handbook provides practical guidance for implementing SDG 4 (Quality Education) at organizational and community levels. It covers inclusive education strategies, lifelong learning opportunities, teacher training, and educational technology integration. The handbook includes self-assessment tools, action planning templates, and case studies from successful SDG 4 initiatives.',
      tags: ['SDG 4', 'Quality Education', 'Handbook', 'Implementation'],
      featured: true,
      pages: 156
    },
    {
      id: 11,
      title: 'Multilingual Content Adaptation: A Practical Guide',
      author: 'Dr. Amanda White, Prof. Dr. Samir Albuny',
      date: '2026-03-20',
      category: 'guides',
      readTime: '18 min read',
      excerpt: 'Step-by-step guide for adapting educational content across languages and cultures.',
      content: 'This practical guide provides methodologies for adapting educational content to different language environments while maintaining cultural sensitivity. It covers translation strategies, localization techniques, and quality assurance processes. The guide includes checklists, style guides, and case studies from successful multilingual education projects.',
      tags: ['Multilingual', 'Content Adaptation', 'Localization', 'Translation'],
      featured: false
    },

    // Additional Research Papers
    {
      id: 12,
      title: 'Renewable Energy Transition in Developing Economies',
      author: 'Prof. Dr. Maria Garcia',
      date: '2026-02-10',
      category: 'research',
      readTime: '20 min read',
      excerpt: 'Analysis of renewable energy adoption challenges and opportunities in developing countries.',
      content: 'This research examines the barriers and enablers of renewable energy transition in developing economies. It analyzes policy frameworks, financing mechanisms, and technology transfer models. The study includes case studies from solar energy projects in Africa and wind power initiatives in Southeast Asia.',
      tags: ['Renewable Energy', 'Sustainability', 'Climate Action', 'SDG 7'],
      featured: false
    },
    {
      id: 13,
      title: 'Corporate Governance and ESG: Emerging Trends',
      author: 'Prof. Dr. John Smith',
      date: '2026-01-25',
      category: 'research',
      readTime: '16 min read',
      excerpt: 'Examining the evolution of corporate governance and ESG practices in global markets.',
      content: 'This paper analyzes emerging trends in corporate governance and Environmental, Social, and Governance (ESG) practices. It examines regulatory developments, investor expectations, and corporate responses across different regions. The research provides recommendations for organizations seeking to enhance their governance frameworks and ESG performance.',
      tags: ['Corporate Governance', 'ESG', 'Sustainability', 'Compliance'],
      featured: false
    },
    {
      id: 14,
      title: 'Cybersecurity Capacity Building in Developing Nations',
      author: 'Dr. Susan Chen',
      date: '2026-03-05',
      category: 'research',
      readTime: '15 min read',
      excerpt: 'Research on cybersecurity challenges and capacity development needs in developing countries.',
      content: 'This study examines the cybersecurity landscape in developing nations, identifying key vulnerabilities and capacity gaps. It proposes a framework for building cybersecurity capabilities through education, training, and international cooperation. The research includes recommendations for policymakers and development organizations.',
      tags: ['Cybersecurity', 'Capacity Building', 'Digital Safety', 'SDG 9'],
      featured: false
    },
    {
      id: 15,
      title: 'Public-Private Partnerships for Sustainable Development',
      author: 'Prof. Dr. Richard Kim',
      date: '2026-02-28',
      category: 'research',
      readTime: '18 min read',
      excerpt: 'Analysis of successful PPP models for achieving sustainable development goals.',
      content: 'This research examines the role of public-private partnerships in advancing sustainable development. It analyzes successful PPP models from various sectors including education, healthcare, and infrastructure. The study identifies critical success factors and provides recommendations for structuring effective partnerships.',
      tags: ['PPP', 'Sustainable Development', 'Partnerships', 'SDG 17'],
      featured: false
    }
  ];

  // Stats from documents
  const stats = [
    { number: '15+', label: 'Publications', icon: '📚' },
    { number: '4', label: 'Journal Issues', icon: '📖' },
    { number: '30+', label: 'Contributing Authors', icon: '👥' },
    { number: '15', label: 'Countries', icon: '🌍' }
  ];

  // Filtered articles
  const filteredArticles = activeCategory === 'all' 
    ? articles 
    : articles.filter(article => article.category === activeCategory);
  
  const searchedArticles = filteredArticles.filter(article => 
    article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    article.excerpt.toLowerCase().includes(searchTerm.toLowerCase()) ||
    article.author.toLowerCase().includes(searchTerm.toLowerCase()) ||
    article.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Featured articles
  const featuredArticles = articles.filter(article => article.featured).slice(0, 3);

  // Article modal functions
  const handleReadArticle = (article) => {
    setSelectedArticle(article);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedArticle(null);
  };

  const handleSaveArticle = (article) => {
    console.log('Saving article:', article.title);
    // In real implementation, this would save to user's library
    alert(`Article saved: ${article.title}`);
  };

  const handleDownloadPDF = (article) => {
    console.log('Downloading PDF:', article.title);
    // In real implementation, this would download PDF
    alert(`Downloading PDF: ${article.title}`);
  };

  return (
    <div className="references-page">
      {/* Hero Section - Updated */}
      <section className="references-hero">
        <div className="hero-content">
          <div className="hero-text">
            <h1>Adam International Journal & Publications</h1>
            <p>Peer-reviewed research, annual reports, and capacity development guides from Adam International Academy</p>
          </div>
          <div className="hero-stats">
            <div className="stat">
              <span className="number">{articles.length}+</span>
              <span className="label">Publications</span>
            </div>
            <div className="stat">
              <span className="number">4</span>
              <span className="label">Journal Issues</span>
            </div>
            <div className="stat">
              <span className="number">30+</span>
              <span className="label">Contributors</span>
            </div>
            <div className="stat">
              <span className="number">30+</span>
              <span className="label">Countries</span>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Articles - Updated */}
      <section 
        className="featured-articles"
        ref={el => sectionRefs.current.featured = el}
        data-section="featured"
      >
        <div className={`container ${visibleSections.featured ? 'visible' : ''}`}>
          <div className="section-header">
            <h2>Featured Publications</h2>
            <p>Notable research and thought leadership from Adam International Academy</p>
          </div>
          <div className="featured-articles-grid">
            {featuredArticles.map(article => (
              <div key={article.id} className="featured-article">
                <div className="article-content">
                  <span className="category-tag">
                    {article.category === 'journal' ? '📖 Adam International Journal' : 
                     article.category === 'reports' ? '📊 Annual Report' : '📘 Guide'}
                  </span>
                  <h3>{article.title}</h3>
                  <p>{article.excerpt}</p>
                  <div className="article-meta">
                    <span className="author">By {article.author}</span>
                    <span className="date">{new Date(article.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                    <span className="read-time">{article.readTime}</span>
                  </div>
                  <div className="article-tags">
                    {article.tags.slice(0, 3).map(tag => (
                      <span key={tag} className="tag">{tag}</span>
                    ))}
                  </div>
                </div>
                <button 
                  className="read-more-btn"
                  onClick={() => handleReadArticle(article)}
                  aria-label={`Read ${article.title}`}
                >
                  Read Publication
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Main Content - Updated */}
      <section 
        className="articles-main"
        ref={el => sectionRefs.current.main = el}
        data-section="main"
      >
        <div className={`container ${visibleSections.main ? 'visible' : ''}`}>
          <div className="content-header">
            <div className="search-container">
              <input 
                type="text" 
                placeholder="Search publications by title, author, or topic..." 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
                aria-label="Search publications"
              />
            </div>
            <div className="categories-filter">
              {categories.map(category => (
                <button
                  key={category.id}
                  className={`category-btn ${activeCategory === category.id ? 'active' : ''}`}
                  onClick={() => setActiveCategory(category.id)}
                  aria-label={`Filter by ${category.name}`}
                >
                  <span className="category-icon">{category.icon}</span>
                  <span className="category-name">{category.name}</span>
                  <span className="category-count">({category.count})</span>
                </button>
              ))}
            </div>
          </div>

          <div className="articles-list">
            {searchedArticles.map(article => (
              <article key={article.id} className="article-item">
                <div className="article-info">
                  <span className="category">
                    {article.category === 'journal' ? '📖 Adam International Journal' : 
                     article.category === 'reports' ? '📊 Annual Report' : 
                     article.category === 'guides' ? '📘 Guide' : '🔬 Research Paper'}
                  </span>
                  <h3>{article.title}</h3>
                  <p className="excerpt">{article.excerpt}</p>
                  <div className="article-details">
                    <span className="author">By {article.author}</span>
                    <span className="date">{new Date(article.date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}</span>
                    <span className="read-time">{article.readTime}</span>
                  </div>
                  <div className="article-tags">
                    {article.tags.map(tag => (
                      <span key={tag} className="tag">{tag}</span>
                    ))}
                  </div>
                  {article.issn && (
                    <div className="publication-meta">
                      <span className="issn">ISSN: {article.issn}</span>
                    </div>
                  )}
                </div>
                <div className="article-actions">
                  <button 
                    className="read-btn"
                    onClick={() => handleReadArticle(article)}
                    aria-label={`Read ${article.title}`}
                  >
                    Read
                  </button>
                  <button 
                    className="save-btn"
                    onClick={() => handleSaveArticle(article)}
                    aria-label={`Save ${article.title}`}
                  >
                    Save
                  </button>
                </div>
              </article>
            ))}
          </div>

          {searchedArticles.length === 0 && (
            <div className="no-results">
              <h3>No publications found</h3>
              <p>Try adjusting your search or filter criteria</p>
            </div>
          )}
        </div>
      </section>

      {/* Journal Information Section - New */}
      <section 
        className="journal-info"
        ref={el => sectionRefs.current.journal = el}
        data-section="journal"
      >
        <div className={`container ${visibleSections.journal ? 'visible' : ''}`}>
          <div className="journal-content">
            <div className="journal-header">
              <h2>Adam International Journal</h2>
              <p className="journal-issn">ISSN: 2757-8912</p>
            </div>
            <div className="journal-details">
              <div className="journal-description">
                <p>A peer-reviewed scientific journal published quarterly by Adam Center for Research and Studies. The journal features research on sustainable development, capacity building, multilingual education, and international relations.</p>
                <ul className="journal-features">
                  <li>✓ Quarterly publication (January, April, July, October)</li>
                  <li>✓ Peer-reviewed by international experts</li>
                  <li>✓ Multilingual abstracts (English, Arabic, Turkish)</li>
                  <li>✓ Open access for researchers worldwide</li>
                </ul>
              </div>
              <div className="journal-call">
                <h3>Submit Your Research</h3>
                <p>We welcome submissions from researchers worldwide. Visit our submission guidelines page for more information.</p>
                <button className="journal-submit-btn">Submission Guidelines</button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section - Updated */}
      <section 
        className="stats-section"
        ref={el => sectionRefs.current.stats = el}
        data-section="stats"
      >
        <div className={`container ${visibleSections.stats ? 'visible' : ''}`}>
          <div className="stats-grid">
            {stats.map((stat, index) => (
              <div key={index} className="stat-card">
                <div className="stat-icon">{stat.icon}</div>
                <div className="stat-number">{stat.number}</div>
                <div className="stat-label">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter Section - Updated */}
      <section 
        className="newsletter-section"
        ref={el => sectionRefs.current.newsletter = el}
        data-section="newsletter"
      >
        <div className={`container ${visibleSections.newsletter ? 'visible' : ''}`}>
          <div className="newsletter-content">
            <h2>Stay Updated with Latest Research</h2>
            <p>Subscribe to receive notifications about new journal issues, research papers, and publications</p>
            <div className="newsletter-form">
              <input 
                type="email" 
                placeholder="Enter your email address" 
                aria-label="Email for newsletter"
              />
              <button className="subscribe-btn">Subscribe</button>
            </div>
            <p className="newsletter-note">We respect your privacy. Unsubscribe at any time.</p>
          </div>
        </div>
      </section>

      {/* Resources Section - Updated */}
      <section 
        className="resources-section"
        ref={el => sectionRefs.current.resources = el}
        data-section="resources"
      >
        <div className={`container ${visibleSections.resources ? 'visible' : ''}`}>
          <div className="section-header">
            <h2>Additional Resources</h2>
            <p>Explore more research and publication resources</p>
          </div>
          <div className="resources-grid">
            <div className="resource-item">
              <h3>📚 Adam Center Library</h3>
              <p>Access our electronic library with 100,000+ books, sources, and research papers in applied, human, and social sciences.</p>
              <a href="#" className="resource-link">Explore Library →</a>
            </div>
            <div className="resource-item">
              <h3>🎓 Research Partnerships</h3>
              <p>Collaborative research initiatives with Sabahattin Zaim University, Medipol University, and international partners.</p>
              <a href="#" className="resource-link">View Partnerships →</a>
            </div>
            <div className="resource-item">
              <h3>📝 Publication Guidelines</h3>
              <p>Guidelines for submitting research to Adam International Journal and other Academy publications.</p>
              <a href="#" className="resource-link">Download Guidelines →</a>
            </div>
          </div>
        </div>
      </section>

      {/* Article Modal - New */}
      {isModalOpen && selectedArticle && (
        <div className="article-modal">
          <div className="modal-overlay" onClick={closeModal}></div>
          <div className="modal-content">
            <button className="modal-close" onClick={closeModal} aria-label="Close modal">×</button>
            <div className="modal-header">
              <h2>{selectedArticle.title}</h2>
              <p className="modal-author">By {selectedArticle.author}</p>
              {selectedArticle.journal && <p className="modal-journal">{selectedArticle.journal}</p>}
            </div>
            <div className="modal-body">
              <div className="modal-meta">
                <span>📅 {new Date(selectedArticle.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                <span>⏱️ {selectedArticle.readTime}</span>
                {selectedArticle.issn && <span>📖 ISSN: {selectedArticle.issn}</span>}
                {selectedArticle.pages && <span>📄 {selectedArticle.pages} pages</span>}
              </div>
              <div className="modal-tags">
                {selectedArticle.tags.map(tag => (
                  <span key={tag} className="modal-tag">{tag}</span>
                ))}
              </div>
              <div className="modal-excerpt">
                <h3>Abstract</h3>
                <p>{selectedArticle.excerpt}</p>
              </div>
              <div className="modal-content-text">
                <h3>Full Article</h3>
                <p>{selectedArticle.content}</p>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn outline" onClick={closeModal}>Close</button>
              <button className="btn primary" onClick={() => handleDownloadPDF(selectedArticle)}>Download PDF</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default References;